import CloseIcon from "@mui/icons-material/Close";
import CloseFullscreenOutlinedIcon from "@mui/icons-material/CloseFullscreenOutlined";
import MinimizeOutlinedIcon from "@mui/icons-material/MinimizeOutlined";
import OpenInFullOutlinedIcon from "@mui/icons-material/OpenInFullOutlined";
import { Box, IconButton } from "@mui/material";
import type { MouseEvent, ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { NEUTRAL } from "@/theme/tokens";
import { headerActionIconButtonsStyle, headerTypographyStyle, MultiSizePopupState, minimizedViewMaximumTitleWidth, type PreviousStateType } from "./utils";

type Props = {
	title: ReactNode;
	onStateChange: (e: MouseEvent<HTMLElement>, newSizeState: MultiSizePopupState) => void;
	sizeState: MultiSizePopupState;
	previousState: PreviousStateType;
	onClose: (e: MouseEvent<HTMLElement>) => void;
	disableHide?: boolean;
	disableMinimize?: boolean;
	disableMaximize?: boolean;
	/** The hide, resize and close buttons are shown disabled (`MultiSizePopup`'s `headerButtonsDisabled`). */
	buttonsDisabled?: boolean;
	/** Extra controls rendered next to the built-in hide/resize/close buttons. Only visible when maximized. */
	headerActions?: ReactNode;
	/** The title element's id, which the dialog is labelled by. */
	titleId: string;
	/** The popup's test id; the buttons take `-hide`, `-resize` and `-close` after it. Absent → no button test ids. */
	dataTestId?: string;
};

export const MultiSizePopupHeader = ({
	sizeState,
	onStateChange,
	onClose,
	title,
	previousState,
	disableMinimize = false,
	disableHide = false,
	disableMaximize = false,
	buttonsDisabled = false,
	headerActions,
	titleId,
	dataTestId,
}: Props) => {
	const { t } = useTranslation();
	const partTestId = (part: string): string | undefined => (dataTestId === undefined ? undefined : `${dataTestId}-${part}`);
	const handleSizeStateChange = (e: MouseEvent<HTMLElement>, clickOrigin: "hiddenButton" | "sizeToggleButton") => {
		e.stopPropagation();
		if (clickOrigin === "hiddenButton") {
			if (sizeState === MultiSizePopupState.HIDDEN) {
				onStateChange(e, previousState);
			} else {
				onStateChange(e, MultiSizePopupState.HIDDEN);
			}
		} else {
			if (previousState === MultiSizePopupState.MAXIMIZED) {
				onStateChange(e, MultiSizePopupState.MINIMIZED);
			} else if (previousState === MultiSizePopupState.MINIMIZED) {
				onStateChange(e, MultiSizePopupState.MAXIMIZED);
			}
		}
	};

	const isHeaderBackgroundClickable = sizeState === MultiSizePopupState.HIDDEN && !buttonsDisabled;

	return (
		<Box
			sx={{
				width: "100%",
				height: "fit-content",
				display: "flex",
				justifyContent: "space-between",
				alignItems: "center",
				p: "8px 12px",
				backgroundColor: NEUTRAL[50],
				":hover": {
					cursor: isHeaderBackgroundClickable ? "pointer" : "default",
					backgroundColor: isHeaderBackgroundClickable ? NEUTRAL[100] : NEUTRAL[50],
				},
				transition: "background-color 0.5s ease",
			}}
			onClick={(e) => {
				isHeaderBackgroundClickable ? handleSizeStateChange(e, "hiddenButton") : null;
			}}
		>
			<Box
				id={titleId}
				title={typeof title === "string" ? title : undefined}
				sx={{
					...headerTypographyStyle,
					maxWidth: sizeState !== MultiSizePopupState.MAXIMIZED ? minimizedViewMaximumTitleWidth : "100%",
					overflow: "hidden",
					textOverflow: "ellipsis",
					whiteSpace: "nowrap",
				}}
			>
				{title}
			</Box>
			<Box
				sx={{
					...headerTypographyStyle,
					...headerActionIconButtonsStyle,
					display: "flex",
					alignItems: "center",
					gap: 0.5,
				}}
			>
				{sizeState === MultiSizePopupState.MAXIMIZED && headerActions}
				{!disableHide && (
					<IconButton
						size={"small"}
						color={"inherit"}
						disabled={buttonsDisabled}
						// Hidden, the same button brings the popup back to its last size.
						aria-label={sizeState === MultiSizePopupState.HIDDEN ? t("system.popup.restore") : t("system.action.hide")}
						data-testid={partTestId("hide")}
						onClick={(e) => {
							e.stopPropagation();
							handleSizeStateChange(e, "hiddenButton");
						}}
					>
						<MinimizeOutlinedIcon fontSize={"inherit"} color={"inherit"} />
					</IconButton>
				)}
				{!disableMinimize && !disableMaximize && (
					<IconButton
						size={"small"}
						color={"inherit"}
						disabled={buttonsDisabled}
						aria-label={previousState === MultiSizePopupState.MAXIMIZED ? t("system.popup.minimize") : t("system.popup.maximize")}
						data-testid={partTestId("resize")}
						onClick={(e) => {
							e.stopPropagation();
							handleSizeStateChange(e, "sizeToggleButton");
						}}
					>
						{previousState === MultiSizePopupState.MAXIMIZED ? (
							<CloseFullscreenOutlinedIcon fontSize={"inherit"} color={"inherit"} />
						) : previousState === MultiSizePopupState.MINIMIZED ? (
							<OpenInFullOutlinedIcon fontSize={"inherit"} color={"inherit"} />
						) : null}
					</IconButton>
				)}
				<IconButton
					size={"small"}
					color={"inherit"}
					disabled={buttonsDisabled}
					aria-label={t("close")}
					data-testid={partTestId("close")}
					onClick={(e) => {
						e.stopPropagation();
						onClose(e);
					}}
				>
					<CloseIcon fontSize={"inherit"} color={"inherit"} />
				</IconButton>
			</Box>
		</Box>
	);
};
